import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import accountingService from '../../services/accountingService';
import realDataAccountingService from '../../services/realDataAccountingService';
import { getCurrencySymbolFromSchool } from '../../utils/currencyFormatter';
import {
  setTrialBalance,
  setLoading,
  setError as setReduxError,
  setPrintData
} from '../../redux/accounting';

const TrialBalance = () => {
  const dispatch = useDispatch();
  const { trialBalance, loading: reduxLoading } = useSelector(state => state.accounting);
  const schooldetails = useSelector(state => state.schooldetails || []);
  
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLoadingTrialBalance, setIsLoadingTrialBalance] = useState(false);
  const loadTrialBalanceRef = useRef(null);

  const currentSchool = schooldetails && schooldetails.length > 0 ? schooldetails[0] : null;
  
  // Get currency symbol from school details
  const currencySymbol = getCurrencySymbolFromSchool(schooldetails);

  // Get Redux data at component level - using correct structure like BalanceSheet
  // Handle both data structures: state.students?.data || state.students
  const studentsData = useSelector(state => state.students?.data || state.students || []);
  const teachersData = useSelector(state => state.teachers?.data || state.teachers || []);
  const expensesData = useSelector(state => state.expenses?.data || state.expenses || []);
  const salesData = useSelector(state => state.sales?.sales || state.sales || []);
  const assetsData = useSelector(state => state.assets?.assets || state.assets || []);
  const classData = useSelector(state => state.class?.data || state.class || []);
  
  // Create reduxData object - memoized to prevent unnecessary re-renders
  const reduxData = useMemo(() => ({
    students: Array.isArray(studentsData) ? studentsData : [],
    teachers: Array.isArray(teachersData) ? teachersData : [],
    expenses: Array.isArray(expensesData) ? expensesData : [],
    sales: Array.isArray(salesData) ? salesData : [],
    assets: Array.isArray(assetsData) ? assetsData : [],
    class: Array.isArray(classData) ? classData : []
  }), [studentsData, teachersData, expensesData, salesData, assetsData, classData]);

  const loadTrialBalance = useCallback(async () => {
    if (!currentSchool?.schoolID || isLoadingTrialBalance) return;
    
    setIsLoadingTrialBalance(true);
    setLoading(true);
    setError(null);
    // Ensure we dispatch a valid action
    try {
      const loadingAction = setLoading({ trialBalance: true });
      if (loadingAction && typeof loadingAction === 'object' && loadingAction.type) {
        dispatch(loadingAction);
      } else {
        console.error('Invalid loading action:', loadingAction);
      }
    } catch (err) {
      console.error('Error dispatching loading action:', err);
    }
    
    try {
      console.log('🔄 Loading trial balance from Redux for school:', currentSchool.schoolID);
      console.log('📊 Redux data available:', {
        students: reduxData.students?.length || 0,
        teachers: reduxData.teachers?.length || 0,
        expenses: reduxData.expenses?.length || 0,
        sales: reduxData.sales?.length || 0,
        assets: reduxData.assets?.length || 0,
        class: reduxData.class?.length || 0
      });
      
      // Add timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Trial balance generation timed out after 30 seconds')), 30000);
      });
      
      // Generate trial balance from Redux data with timeout
      const dataPromise = realDataAccountingService.generateRealTrialBalance(
        currentSchool?.schoolID,
        startDate,
        endDate,
        reduxData
      );
      
      const data = await Promise.race([dataPromise, timeoutPromise]);
      
      console.log('📊 Redux trial balance data received:', data);
      // Ensure data is valid before dispatching
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        try {
          const trialBalanceAction = setTrialBalance(data);
          if (trialBalanceAction && typeof trialBalanceAction === 'object' && trialBalanceAction.type) {
            dispatch(trialBalanceAction);
          } else {
            console.error('Invalid trial balance action:', trialBalanceAction);
            throw new Error('Invalid trial balance action');
          }
        } catch (dispatchErr) {
          console.error('Error dispatching trial balance:', dispatchErr);
          throw dispatchErr;
        }
      } else {
        console.error('Invalid trial balance data:', data);
        throw new Error('Invalid trial balance data received');
      }
    } catch (err) {
      console.error('Redux trial balance error:', err);
      const errorMessage = err.message?.includes('timed out') 
        ? 'Trial balance generation is taking too long. Please try again or check your data.'
        : 'Failed to load trial balance: ' + (err.message || 'Unknown error');
      setError(errorMessage);
      // Ensure we dispatch a valid error action
      try {
        const errorAction = setReduxError(errorMessage);
        if (errorAction && typeof errorAction === 'object' && errorAction.type) {
          dispatch(errorAction);
        } else {
          console.error('Invalid error action:', errorAction);
        }
      } catch (dispatchErr) {
        console.error('Error dispatching error action:', dispatchErr);
      }
    } finally {
      // Always set loading to false
      setLoading(false);
      setIsLoadingTrialBalance(false);
      // Ensure we dispatch a valid loading action
      try {
        const loadingAction = setLoading({ trialBalance: false });
        if (loadingAction && typeof loadingAction === 'object' && loadingAction.type) {
          dispatch(loadingAction);
        } else {
          console.error('Invalid loading action:', loadingAction);
        }
      } catch (dispatchErr) {
        console.error('Error dispatching loading action:', dispatchErr);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSchool?.schoolID, startDate, endDate, reduxData, dispatch]);

  // Store the latest loadTrialBalance in a ref
  useEffect(() => {
    loadTrialBalanceRef.current = loadTrialBalance;
  }, [loadTrialBalance]);

  useEffect(() => {
    // Only run if schoolID is available and not already loading
    if (currentSchool?.schoolID && !isLoadingTrialBalance && loadTrialBalanceRef.current) {
      // Call the function and handle any errors
      const loadFn = loadTrialBalanceRef.current;
      if (typeof loadFn === 'function') {
        loadFn().catch(err => {
          console.error('Error in loadTrialBalance:', err);
          setLoading(false);
          setIsLoadingTrialBalance(false);
          setError('Failed to load trial balance: ' + (err.message || 'Unknown error'));
          // Ensure we dispatch a valid action
          try {
            dispatch(setLoading({ trialBalance: false }));
          } catch (dispatchErr) {
            console.error('Error dispatching loading state:', dispatchErr);
          }
        });
      }
    } else if (!currentSchool?.schoolID) {
      // Clear loading state if no school selected
      setLoading(false);
      setIsLoadingTrialBalance(false);
      setError(null);
      // Clear Redux loading state
      try {
        dispatch(setLoading({ trialBalance: false }));
      } catch (err) {
        console.error('Error clearing Redux loading:', err);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSchool?.schoolID, startDate, endDate]); // Don't include dispatch to prevent re-renders

  const handleRefresh = () => {
    loadTrialBalance();
  };

  const handlePrint = () => {
    if (!trialBalance) {
      setError('No trial balance data available to print');
      return;
    }

    // Create a print-friendly window
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setError('Please allow popups to print the trial balance');
      return;
    }

    const schoolName = currentSchool?.schoolname || currentSchool?.schoolName || 'School';
    const period = `For the period from ${accountingService.formatDate(startDate)} to ${accountingService.formatDate(endDate)}`;

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Trial Balance - ${schoolName}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { text-align: center; margin-bottom: 5px; }
            h2 { text-align: center; color: #666; margin-top: 5px; margin-bottom: 20px; }
            .trial-info { text-align: center; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .total-row { font-weight: bold; background-color: #f5f5f5; }
            .right-align { text-align: right; }
            .balanced { color: green; font-weight: bold; }
            .unbalanced { color: red; font-weight: bold; }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>${schoolName}</h1>
          <h2>Trial Balance</h2>
          <div class="trial-info">${period}</div>
          <div class="trial-info">
            Generated: ${trialBalance.generatedAt ? new Date(trialBalance.generatedAt).toLocaleString() : new Date().toLocaleString()}
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Account Code</th>
                <th>Account Name</th>
                <th>Account Type</th>
                <th class="right-align">Debit Balance</th>
                <th class="right-align">Credit Balance</th>
              </tr>
            </thead>
            <tbody>
              ${trialBalance.trialBalance.map(account => `
                <tr>
                  <td>${account.accountCode}</td>
                  <td>${account.accountName}</td>
                  <td>${account.accountType}</td>
                  <td class="right-align">${account.debitBalance > 0 ? accountingService.formatCurrency(account.debitBalance) : '-'}</td>
                  <td class="right-align">${account.creditBalance > 0 ? accountingService.formatCurrency(account.creditBalance) : '-'}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="3"><strong>TOTALS</strong></td>
                <td class="right-align"><strong>${accountingService.formatCurrency(totalDebits)}</strong></td>
                <td class="right-align"><strong>${accountingService.formatCurrency(totalCredits)}</strong></td>
              </tr>
            </tbody>
          </table>
          
          <div class="trial-info ${Math.abs(totalDebits - totalCredits) < 0.01 ? 'balanced' : 'unbalanced'}">
            ${Math.abs(totalDebits - totalCredits) < 0.01 
              ? '✓ Trial Balance is Balanced' 
              : `✗ Trial Balance is NOT Balanced (Difference: ${accountingService.formatCurrency(Math.abs(totalDebits - totalCredits))})`}
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    
    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleExport = async (format = 'pdf') => {
    if (!trialBalance) {
      setError('No trial balance data available to export');
      return;
    }

    try {
      // Check if exportReport method exists
      if (accountingService.exportReport && typeof accountingService.exportReport === 'function') {
        const data = await accountingService.exportReport('trial-balance', trialBalance, format);
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `trial_balance_${startDate}_to_${endDate}.${format}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        setError('Export functionality is not available. Please use the Print function instead.');
      }
    } catch (err) {
      console.error('Export error:', err);
      setError(`Error exporting ${format.toUpperCase()}: ${err.message || 'Unknown error'}`);
    }
  };

  const getAccountTypeColor = (accountType) => {
    const colors = {
      'Asset': '#1976d2',
      'Liability': '#d32f2f',
      'Equity': '#388e3c',
      'Revenue': '#f57c00',
      'Expense': '#9c27b0'
    };
    return colors[accountType] || '#666';
  };

  const calculateTotals = () => {
    if (!trialBalance?.trialBalance) return { totalDebits: 0, totalCredits: 0 };
    
    const totalDebits = trialBalance.trialBalance.reduce((sum, account) => sum + account.debitBalance, 0);
    const totalCredits = trialBalance.trialBalance.reduce((sum, account) => sum + account.creditBalance, 0);
    
    return { totalDebits, totalCredits };
  };

  const { totalDebits, totalCredits } = calculateTotals();

  // Check both local and Redux loading states
  const isLoading = loading || (reduxLoading?.trialBalance === true);

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading trial balance...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={handleRefresh}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!trialBalance && !isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6">No Trial Balance data available.</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
          Select a date range and click refresh to generate trial balance.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Redux Data Status: Students: {reduxData.students.length}, Teachers: {reduxData.teachers.length}, 
          Expenses: {reduxData.expenses.length}, Sales: {reduxData.sales.length}, Assets: {reduxData.assets.length}
        </Typography>
        <Button variant="contained" onClick={handleRefresh} disabled={isLoading}>
          {isLoading ? 'Loading...' : 'Generate Trial Balance'}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            Trial Balance
          </Typography>
          {trialBalance?.isSampleData && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
              ⚠️ Showing sample data - No real financial data found in Redux
            </Typography>
          )}
        </Box>
        <Box>
          <Tooltip title="Refresh Data">
            <IconButton onClick={handleRefresh} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                  disabled={!trialBalance}
                >
                  Print
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('pdf')}
                  disabled={!trialBalance}
                >
                  PDF
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('excel')}
                  disabled={!trialBalance}
                >
                  Excel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Trial Balance Table */}
      {trialBalance && (
        <Paper>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">
              Trial Balance
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Period: {accountingService.formatDate(startDate)} to {accountingService.formatDate(endDate)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Generated: {trialBalance.generatedAt 
                ? (trialBalance.generatedAt instanceof Date 
                  ? accountingService.formatDate(trialBalance.generatedAt.toISOString().split('T')[0])
                  : accountingService.formatDate(trialBalance.generatedAt))
                : new Date().toLocaleString()}
            </Typography>
            {currentSchool && (
              <Typography variant="body2" color="text.secondary">
                School: {currentSchool.schoolname || currentSchool.schoolName || 'N/A'}
              </Typography>
            )}
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Account Code</TableCell>
                  <TableCell>Account Name</TableCell>
                  <TableCell>Account Type</TableCell>
                  <TableCell align="right">Debit Balance</TableCell>
                  <TableCell align="right">Credit Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trialBalance.trialBalance.map((account, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {account.accountCode}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {account.accountName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={account.accountType}
                        size="small"
                        sx={{ 
                          backgroundColor: getAccountTypeColor(account.accountType),
                          color: 'white'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {account.debitBalance > 0 ? accountingService.formatCurrency(account.debitBalance) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {account.creditBalance > 0 ? accountingService.formatCurrency(account.creditBalance) : '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Totals */}
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', backgroundColor: '#f5f5f5' }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="h6" align="right">
                  Total Debits: {accountingService.formatCurrency(totalDebits)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6" align="right">
                  Total Credits: {accountingService.formatCurrency(totalCredits)}
                </Typography>
              </Grid>
            </Grid>
            
            {/* Balance Check */}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              {trialBalance?.isBalanced !== false && Math.abs(totalDebits - totalCredits) < 0.01 ? (
                <Chip
                  label="✓ Trial Balance is Balanced"
                  color="success"
                  icon={<AssessmentIcon />}
                />
              ) : (
                <Chip
                  label={`✗ Trial Balance is NOT Balanced (Difference: ${accountingService.formatCurrency(Math.abs(totalDebits - totalCredits))})`}
                  color="error"
                  icon={<AssessmentIcon />}
                />
              )}
              {trialBalance?.difference !== undefined && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Total Debits: {accountingService.formatCurrency(totalDebits)} | 
                  Total Credits: {accountingService.formatCurrency(totalCredits)} | 
                  Difference: {accountingService.formatCurrency(trialBalance.difference)}
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>
      )}

      {!trialBalance && !isLoading && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No trial balance data available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a date range and click refresh to generate trial balance
            </Typography>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default TrialBalance;
